import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../ui/select";
import {
  Star,
  Zap,
  Building,
  ShieldCheck,
  PiggyBank,
  Info,
  Layers,
} from "lucide-react";

const iconMap = {
  star: Star,
  zap: Zap,
  building: Building,
  "shield-check": ShieldCheck,
  "piggy-bank": PiggyBank,
  info: Info,
  layers: Layers,
};

function ItemCard({ item, highlightClass }) {
  const Icon = iconMap[item?.icon] || Star;
  const imagePosition = item?.imagePosition || "center";
  const objectPositionValue = getObjectPositionValue(imagePosition);
  
  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Icon className={highlightClass || ""} size={18} />
          <CardTitle className="text-base">{item?.title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {item?.imageurl ? (
          <img 
            src={item.imageurl} 
            alt={item?.title || "imagen"} 
            className="w-full h-32 object-cover rounded mb-3" 
            style={{ objectPosition: objectPositionValue }}
          />
        ) : null}
        <div className="flex flex-wrap gap-2 mb-2">
          {(item?.highlights || []).map((h, idx) => (
            <Badge key={idx} variant="outline" className={highlightClass || ""}>
              {h}
            </Badge>
          ))}
        </div>
        <p className="text-sm text-muted-foreground">{item?.description}</p>
      </CardContent>
    </Card>
  );
}

const ELEMENT_OPTIONS = [
  "carousel",
  "grid",
  "features",
  "only-section",
  "tabs",
  "comparison-table",
  "accordion",
  "info-cards",
  "hero-cta",
  "steps",
  "value-cards",
];

const FANCY_OPTIONS = [
  "fancyyool-highlight-blue",
  "fancyyool-highlight-violet",
  "fancyyool-highlight-cyan",
  "fancyyool-highlight-orange",
  "fancyyool-highlight-indigo",
  "fancyyool-highlight-red",
  "fancyyool-highlight-pink",
  "fancyyool-highlight-purple",
  "fancyyool-highlight-sky",
  "fancyyool-highlight-teal",
  "fancyyool-highlight-greenchill",
  "fancyyool-highlight-lime",
  "fancyyool-highlight-slate",
  "fancyyool-highlight-gray",
];

const IMAGE_POSITION_OPTIONS = [
  { value: "center", label: "Centro" },
  { value: "top", label: "Arriba" },
  { value: "middletop", label: "Medio alto" },
  { value: "bottom", label: "Abajo" },
  { value: "middlebottom", label: "Medio bajo" },
  { value: "left", label: "Izquierda" },
  { value: "middleleft", label: "Medio izquierda" },
  { value: "right", label: "Derecha" },
  { value: "middleright", label: "Medio derecha" },
  { value: "top-left", label: "Esquina superior izquierda" },
  { value: "top-right", label: "Esquina superior derecha" },
  { value: "bottom-left", label: "Esquina inferior izquierda" },
  { value: "bottom-right", label: "Esquina inferior derecha" },
];

const getObjectPositionValue = (position) => {
  const positionMap = {
    center: "center",
    top: "center top",
    middletop: "center 25%",
    bottom: "center bottom",
    middlebottom: "center 75%",
    left: "left center",
    middleleft: "25% center",
    right: "right center",
    middleright: "75% center",
    "top-left": "left top",
    "top-right": "right top",
    "bottom-left": "left bottom",
    "bottom-right": "right bottom",
  };
  return positionMap[position] || "center";
};

export default function SectionPreview({ productDescription, onChange }) {
  const sections = productDescription?.sections || [];

  const updateSectionMeta = (sectionId, field, value) => {
    const updated = { ...productDescription };
    updated.sections = (updated.sections || []).map((s) =>
      s.id === sectionId ? { ...s, [field]: value } : s
    );
    onChange?.(updated);
  };

  const updateSectionItemByIndex = (sectionId, itemIndex, field, value) => {
    const updated = { ...(productDescription || {}) };
    updated.sections = (updated.sections || []).map((s) => {
      if (s.id !== sectionId) return s;
      const items = (s.items || []).map((it, idx) =>
        idx === itemIndex ? { ...it, [field]: value } : it
      );
      return { ...s, items };
    });
    onChange?.(updated);
  };

  const addSectionItemHighlight = (sectionId, itemIndex) => {
    const section = (productDescription?.sections || []).find((s) => s.id === sectionId);
    const current = section?.items?.[itemIndex]?.highlights || [];
    updateSectionItemByIndex(sectionId, itemIndex, "highlights", [...current, ""]);
  };

  const updateSectionItemHighlightAt = (sectionId, itemIndex, highlightIndex, value) => {
    const section = (productDescription?.sections || []).find((s) => s.id === sectionId);
    const current = section?.items?.[itemIndex]?.highlights || [];
    const next = current.map((h, idx) => (idx === highlightIndex ? value : h));
    updateSectionItemByIndex(sectionId, itemIndex, "highlights", next);
  };

  const removeSectionItemHighlightAt = (sectionId, itemIndex, highlightIndex) => {
    const section = (productDescription?.sections || []).find((s) => s.id === sectionId);
    const current = section?.items?.[itemIndex]?.highlights || [];
    const next = current.filter((_, idx) => idx !== highlightIndex);
    updateSectionItemByIndex(sectionId, itemIndex, "highlights", next);
  };

  // Funciones para manejar requirements de info-cards
  const addSectionItemRequirement = (sectionId, itemIndex) => {
    const section = (productDescription?.sections || []).find((s) => s.id === sectionId);
    const current = section?.items?.[itemIndex]?.requirements || [];
    updateSectionItemByIndex(sectionId, itemIndex, "requirements", [...current, ""]);
  };

  const updateSectionItemRequirementAt = (sectionId, itemIndex, reqIndex, value) => {
    const section = (productDescription?.sections || []).find((s) => s.id === sectionId);
    const current = section?.items?.[itemIndex]?.requirements || [];
    const next = current.map((r, idx) => (idx === reqIndex ? value : r));
    updateSectionItemByIndex(sectionId, itemIndex, "requirements", next);
  };

  const removeSectionItemRequirementAt = (sectionId, itemIndex, reqIndex) => {
    const section = (productDescription?.sections || []).find((s) => s.id === sectionId);
    const current = section?.items?.[itemIndex]?.requirements || [];
    const next = current.filter((_, idx) => idx !== reqIndex);
    updateSectionItemByIndex(sectionId, itemIndex, "requirements", next);
  };

  const addSection = () => {
    const updated = { ...(productDescription || {}) };
    const newSection = {
      id: `section-${Date.now()}`,
      label: "Nueva Sección",
      type: "nueva-seccion",
      element: "grid",
      color: "#2563eb",
      items: [],
      fancyyoolClass: "fancyyool-highlight-blue"
    };
    updated.sections = [...(updated.sections || []), newSection];
    onChange?.(updated);
  };

  const removeSection = (sectionId) => {
    const updated = { ...(productDescription || {}) };
    updated.sections = (updated.sections || []).filter((s) => s.id !== sectionId);
    onChange?.(updated);
  };

  const addSectionItem = (sectionId) => {
    const updated = { ...(productDescription || {}) };
    const section = (productDescription?.sections || []).find((s) => s.id === sectionId);
    const isInfoCards = section?.element === "info-cards";
    const isValueCards = section?.element === "value-cards";
    const isSteps = section?.element === "steps";
    
    updated.sections = (updated.sections || []).map((s) => {
      if (s.id !== sectionId) return s;
      let newItem;
      if (isInfoCards) {
        newItem = {
          id: `item-${Date.now()}`,
          planName: "",
          requirements: [],
          imageurl: "",
        };
      } else if (isValueCards) {
        newItem = {
          id: `item-${Date.now()}`,
          title: "",
          description: "",
        };
      } else if (isSteps) {
        newItem = {
          id: `item-${Date.now()}`,
          number: (s.items?.length || 0) + 1,
          title: "",
          description: "",
          icon: "star",
        };
      } else {
        newItem = {
          id: `item-${Date.now()}`,
          title: "",
          description: "",
          highlights: [],
          icon: "star",
          imageurl: "",
        };
      }
      return { ...s, items: [...(s.items || []), newItem] };
    });
    onChange?.(updated);
  };

  const removeSectionItemByIndex = (sectionId, itemIndex) => {
    const updated = { ...(productDescription || {}) };
    updated.sections = (updated.sections || []).map((s) => {
      if (s.id !== sectionId) return s;
      const items = (s.items || []).filter((_, idx) => idx !== itemIndex);
      return { ...s, items };
    });
    onChange?.(updated);
  };

  const updateHero = (field, value) => {
    const updated = { ...productDescription, hero: { ...(productDescription?.hero || {}), [field]: value } };
    onChange?.(updated);
  };

  const updateHeroComment = (commentIndex, field, value) => {
    const comments = (productDescription?.hero?.comments || []).map((c, idx) =>
      idx === commentIndex ? { ...c, [field]: value } : c
    );
    updateHero("comments", comments);
  };

  const addHeroComment = () => {
    const comments = (productDescription?.hero?.comments || []);
    const newComment = {
      id: `comment-${Date.now()}`,
      type: "hero-comment",
      content: "",
      highlight: []
    };
    updateHero("comments", [...comments, newComment]);
  };

  const removeHeroComment = (commentIndex) => {
    const comments = (productDescription?.hero?.comments || []).filter((_, idx) => idx !== commentIndex);
    updateHero("comments", comments);
  };

  const updateHeroCommentHighlight = (commentIndex, highlightIndex, value) => {
    const comments = (productDescription?.hero?.comments || []).map((c, idx) => {
      if (idx !== commentIndex) return c;
      const highlights = (c.highlight || []).map((h, hIdx) => hIdx === highlightIndex ? value : h);
      return { ...c, highlight: highlights };
    });
    updateHero("comments", comments);
  };

  const addHeroCommentHighlight = (commentIndex) => {
    const comments = (productDescription?.hero?.comments || []).map((c, idx) => {
      if (idx !== commentIndex) return c;
      return { ...c, highlight: [...(c.highlight || []), ""] };
    });
    updateHero("comments", comments);
  };

  const removeHeroCommentHighlight = (commentIndex, highlightIndex) => {
    const comments = (productDescription?.hero?.comments || []).map((c, idx) => {
      if (idx !== commentIndex) return c;
      const highlights = (c.highlight || []).filter((_, hIdx) => hIdx !== highlightIndex);
      return { ...c, highlight: highlights };
    });
    updateHero("comments", comments);
  };

  return (
    <div className="space-y-6 w-full max-w-full overflow-hidden">
      <Tabs defaultValue={sections[0]?.id || "hero"} className="w-full max-w-full">
        <div className="w-full max-w-full overflow-x-auto pb-2 -mx-1 px-1">
          <TabsList className="inline-flex h-10 w-max gap-1 bg-muted p-1 rounded-lg">
            <TabsTrigger value="hero" className="whitespace-nowrap text-xs sm:text-sm px-3">Hero</TabsTrigger>
            {sections.map((section) => (
              <TabsTrigger key={section.id} value={section.id} className="whitespace-nowrap text-xs sm:text-sm px-3">
                {section?.label || section.id}
              </TabsTrigger>
            ))}
            <button
              type="button"
              onClick={addSection}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-xs sm:text-sm px-3 py-1.5 text-muted-foreground hover:bg-white hover:text-foreground transition-colors cursor-pointer"
              title="Agregar nueva sección"
            >
              + Nueva
            </button>
          </TabsList>
        </div>

        {/* Hero Tab */}
        <TabsContent value="hero" className="space-y-4 max-w-full">
          <Card>
            <CardHeader>
              <CardTitle>Previsualización Hero</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <div className="fancyyool-title">{productDescription?.hero?.title}</div>
                <div className="fancyyool-inline-subtitle">{productDescription?.hero?.subtitle}</div>
                <p className="mt-2 text-muted-foreground">{productDescription?.hero?.description}</p>
                <div className="mt-4 flex justify-center gap-2">
                  {productDescription?.hero?.cta?.primary && (
                    <Badge variant="outline">{productDescription?.hero?.cta?.primary}</Badge>
                  )}
                  {productDescription?.hero?.cta?.secondary && (
                    <Badge variant="outline">{productDescription?.hero?.cta?.secondary}</Badge>
                  )}
                </div>
              </div>
              
              {/* Comments Section */}
              {(productDescription?.hero?.comments || []).length > 0 && (
                <div className="mt-6 pt-6 border-t space-y-3">
                  <h4 className="text-sm font-semibold">Comentarios:</h4>
                  {productDescription?.hero?.comments?.map((comment, idx) => (
                    <div key={comment?.id || idx} className="bg-gray-50 p-3 rounded text-sm">
                      <p className="text-gray-700">{comment?.content}</p>
                      {(comment?.highlight || []).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {comment.highlight.map((h, hIdx) => (
                            <Badge key={hIdx} variant="secondary" className="text-xs">{h}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Editar Hero</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm">Título</label>
                  <Input value={productDescription?.hero?.title || ""} onChange={(e) => updateHero("title", e.target.value)} />
                </div>
                <div>
                  <label className="text-sm">Subtítulo</label>
                  <Input value={productDescription?.hero?.subtitle || ""} onChange={(e) => updateHero("subtitle", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm">Descripción</label>
                  <Textarea rows={4} value={productDescription?.hero?.description || ""} onChange={(e) => updateHero("description", e.target.value)} />
                </div>
                <div>
                  <label className="text-sm">CTA Primario</label>
                  <Input value={productDescription?.hero?.cta?.primary || ""} onChange={(e) => updateHero("cta", { ...(productDescription?.hero?.cta || {}), primary: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm">CTA Secundario</label>
                  <Input value={productDescription?.hero?.cta?.secondary || ""} onChange={(e) => updateHero("cta", { ...(productDescription?.hero?.cta || {}), secondary: e.target.value })} />
                </div>
              </div>

              {/* Comments Section */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-semibold">Comentarios</h4>
                  <Button type="button" variant="outline" size="sm" onClick={addHeroComment}>
                    Agregar comentario
                  </Button>
                </div>
                
                {(productDescription?.hero?.comments || []).map((comment, commentIndex) => (
                  <Card key={comment?.id || commentIndex} className="border-dashed">
                    <CardHeader>
                      <CardTitle className="text-base">Comentario {commentIndex + 1}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <label className="text-sm">Contenido</label>
                        <Textarea
                          rows={3}
                          value={comment?.content || ""}
                          onChange={(e) => updateHeroComment(commentIndex, "content", e.target.value)}
                          placeholder="Texto del comentario..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Highlights</label>
                        {(comment?.highlight || []).map((h, highlightIndex) => (
                          <div key={`${comment?.id}-h-${highlightIndex}`} className="flex gap-2">
                            <Input
                              value={h || ""}
                              onChange={(e) => updateHeroCommentHighlight(commentIndex, highlightIndex, e.target.value)}
                              placeholder="Texto a destacar..."
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={() => removeHeroCommentHighlight(commentIndex, highlightIndex)}
                            >
                              ✕
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addHeroCommentHighlight(commentIndex)}
                        >
                          Agregar highlight
                        </Button>
                      </div>

                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeHeroComment(commentIndex)}
                        >
                          Eliminar comentario
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sections Tabs */}
        {sections.map((section) => {
          const highlightClass = section?.fancyyoolClass || "";
          const element = section?.element;
          return (
            <TabsContent key={section.id} value={section.id} className="space-y-4 max-w-full overflow-hidden">
              <Card className="max-w-full overflow-hidden">
                <CardHeader>
                  <CardTitle>{section?.label}</CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  {element === "carousel" && (
                    <ScrollArea className="w-full">
                      <div className="flex gap-3 overflow-x-auto pb-2">
                        {(section?.items || []).map((it, idx) => (
                          <div key={it?.id || idx} className="min-w-[260px]">
                            <ItemCard item={it} highlightClass={highlightClass} />
                          </div>
                        ))}
                      </div>
                      <ScrollBar orientation="horizontal" />
                    </ScrollArea>
                  )}

                  {(element === "grid" || element === "features") && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(section?.items || []).map((it) => (
                        <ItemCard key={it.id} item={it} highlightClass={highlightClass} />
                      ))}
                    </div>
                  )}

                  {element === "only-section" && (
                    <ItemCard item={section?.items?.[0]} highlightClass={highlightClass} />
                  )}

                  {element === "tabs" && (
                    <Tabs defaultValue={section?.tabs?.[0]?.id || "tab-1"}>
                      <TabsList>
                        {(section?.tabs || []).map((t) => (
                          <TabsTrigger key={t.id} value={t.id}>{t.title}</TabsTrigger>
                        ))}
                      </TabsList>
                      {(section?.tabs || []).map((t) => (
                        <TabsContent key={t.id} value={t.id}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Card>
                              <CardHeader>
                                <CardTitle className={highlightClass || ""}>{t.badge}</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="grid grid-cols-2 gap-2">
                                  {(t.metrics || []).map((m, idx) => (
                                    <div key={idx} className="text-sm">
                                      <span className="text-muted-foreground">{m.label}:</span> {m.value}
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardHeader>
                                <CardTitle>Beneficios</CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-2">
                                {(t.benefits || []).map((b) => (
                                  <div key={b.id}>
                                    <div className={highlightClass || ""}>{b.title}</div>
                                    <div className="text-sm text-muted-foreground">{b.description}</div>
                                  </div>
                                ))}
                              </CardContent>
                            </Card>
                          </div>
                        </TabsContent>
                      ))}
                    </Tabs>
                  )}

                  {element === "comparison-table" && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Característica</TableHead>
                          {(section?.features?.[0]?.plans || []).map((p, idx) => (
                            <TableHead key={idx}>{p.plan}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(section?.features || []).map((f) => (
                          <TableRow key={f.id}>
                            <TableCell>{f.name}</TableCell>
                            {(f.plans || []).map((p, idx) => (
                              <TableCell key={idx}>{p.value}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}

                  {element === "accordion" && (
                    <div className="space-y-2">
                      {(section?.items || []).map((it) => (
                        <details key={it.id} className="rounded-md border p-3">
                          <summary className={highlightClass || ""}>{it.title}</summary>
                          <div className="mt-2 text-sm text-muted-foreground">{it.description}</div>
                        </details>
                      ))}
                    </div>
                  )}

                  {element === "steps" && (
                    <div className="space-y-6">
                      <div className="text-center mb-6">
                        <h3 className="text-2xl font-bold text-gray-900">{section?.title}</h3>
                        <p className="text-gray-600 mt-2">{section?.subtitle}</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {(section?.items || []).map((step) => {
                          const StepIcon = iconMap[step?.icon] || Info;
                          return (
                            <div key={step?.id} className="text-center">
                              <div className="flex justify-center mb-4">
                                <div className="w-16 h-16 rounded-full bg-orange-500 flex items-center justify-center text-white text-2xl font-bold">
                                  {step?.number}
                                </div>
                              </div>
                              <h4 className="font-semibold text-lg text-gray-900 mb-2">{step?.title}</h4>
                              <p className="text-sm text-gray-600">{step?.description}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {element === "value-cards" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {(section?.items || []).map((item) => (
                        <div key={item?.id} className="space-y-2">
                          <h4 className="font-bold text-lg text-gray-900">{item?.title}</h4>
                          <p className="text-gray-600 text-sm">{item?.description}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {element === "info-cards" && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {(section?.items || []).map((it) => (
                        <Card key={it.id}>
                          <CardHeader>
                            <CardTitle className={highlightClass || ""}>{it.planName}</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {it?.imageurl ? (
                              <img
                                src={it.imageurl}
                                alt={it?.planName || "imagen"}
                                className="w-full h-32 object-cover rounded"
                                style={{ objectPosition: getObjectPositionValue(it?.imagePosition || "center") }}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            ) : null}
                            <ul className="text-sm list-disc pl-5 space-y-1">
                              {(it.requirements || []).map((r, idx) => (
                                <li key={idx}>{r}</li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  {element === "hero-cta" && (
                    <div className="text-center">
                      <div className="fancyyool-title">{section?.title}</div>
                      <div className="fancyyool-inline-subtitle">{section?.subtitle}</div>
                      <p className="mt-2 text-muted-foreground">{section?.description}</p>
                      <div className="mt-4 flex justify-center gap-2">
                        {(section?.buttons || []).map((btn) => (
                          <Badge key={btn.id} variant="outline" className={section?.fancyyoolClass || ""}>
                            {btn.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fallback: cuando element no está definido o no coincide con ningún tipo conocido */}
                  {!element || !["carousel", "grid", "features", "only-section", "tabs", "comparison-table", "accordion", "info-cards", "hero-cta", "steps", "value-cards"].includes(element) ? (
                    <div className="space-y-4">
                      <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                        Tipo de elemento: <code className="font-mono">{element || "(no definido)"}</code> - Selecciona un tipo en el editor para ver la previsualización correcta.
                      </div>
                      {/* Mostrar items si existen */}
                      {(section?.items || []).length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {(section?.items || []).map((it, idx) => (
                            <ItemCard key={it?.id || idx} item={it} highlightClass={highlightClass} />
                          ))}
                        </div>
                      )}
                      {/* Mostrar contenido de texto si existe */}
                      {section?.content && (
                        <p className="text-muted-foreground">{section.content}</p>
                      )}
                      {section?.description && !section?.items?.length && (
                        <p className="text-muted-foreground">{section.description}</p>
                      )}
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              {/* Simple Editors */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Editar Sección</CardTitle>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => removeSection(section.id)}
                    >
                      Eliminar Sección
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm">Etiqueta</label>
                      <Input value={section?.label || ""} onChange={(e) => updateSectionMeta(section.id, "label", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-sm">Elemento</label>
                      <Select value={section?.element || undefined} onValueChange={(value) => updateSectionMeta(section.id, "element", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona elemento" />
                        </SelectTrigger>
                        <SelectContent>
                          {ELEMENT_OPTIONS.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm">Clase FancyYool</label>
                      <Select value={section?.fancyyoolClass || undefined} onValueChange={(value) => updateSectionMeta(section.id, "fancyyoolClass", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona clase" />
                        </SelectTrigger>
                        <SelectContent>
                          {FANCY_OPTIONS.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm">Icono (sección)</label>
                      <Input value={section?.icon || ""} onChange={(e) => updateSectionMeta(section.id, "icon", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-sm">Tipo</label>
                      <Input value={section?.type || ""} onChange={(e) => updateSectionMeta(section.id, "type", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-sm">Color</label>
                      <Input type="color" value={section?.color || "#000000"} onChange={(e) => updateSectionMeta(section.id, "color", e.target.value)} />
                    </div>
                  </div>

                  {/* Editor de título y subtítulo para steps */}
                  {element === "steps" && (
                    <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div>
                        <label className="text-sm">Título de la Sección</label>
                        <Input value={section?.title || ""} onChange={(e) => updateSectionMeta(section.id, "title", e.target.value)} placeholder="Ej: ¿Cómo funciona?" />
                      </div>
                      <div>
                        <label className="text-sm">Subtítulo</label>
                        <Input value={section?.subtitle || ""} onChange={(e) => updateSectionMeta(section.id, "subtitle", e.target.value)} placeholder="Descripción corta..." />
                      </div>
                    </div>
                  )}

                  {element !== "comparison-table" && element !== "tabs" && element !== "hero-cta" && (
                    <div className="space-y-4">
                      {(section?.items || []).map((it, itemIndex) => (
                        <Card key={it?.id || itemIndex} className="border-dashed">
                          <CardHeader>
                            <CardTitle className="text-base">Ítem {itemIndex + 1}</CardTitle>
                          </CardHeader>
                          <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="space-y-3">
                              {/* Campos para steps */}
                              {element === "steps" ? (
                                <>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="text-sm">ID</label>
                                      <Input value={it?.id || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "id", e.target.value)} />
                                    </div>
                                    <div>
                                      <label className="text-sm">Número</label>
                                      <Input type="number" value={it?.number || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "number", parseInt(e.target.value))} />
                                    </div>
                                    <div className="md:col-span-2">
                                      <label className="text-sm">Título del Paso</label>
                                      <Input value={it?.title || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "title", e.target.value)} placeholder="Ej: Atiéndete con tu sistema de salud" />
                                    </div>
                                    <div className="md:col-span-2">
                                      <label className="text-sm">Descripción</label>
                                      <Textarea value={it?.description || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "description", e.target.value)} rows={3} placeholder="Descripción del paso..." />
                                    </div>
                                    <div>
                                      <label className="text-sm">Icono</label>
                                      <Input value={it?.icon || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "icon", e.target.value)} placeholder="Ej: user, file, check" />
                                    </div>
                                  </div>

                                  <div className="flex justify-end">
                                    <Button type="button" variant="destructive" onClick={() => removeSectionItemByIndex(section.id, itemIndex)}>
                                      Eliminar paso
                                    </Button>
                                  </div>
                                </>
                              ) : element === "value-cards" ? (
                                <>
                                  <div className="grid grid-cols-1 gap-3">
                                    <div>
                                      <label className="text-sm">ID</label>
                                      <Input value={it?.id || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "id", e.target.value)} />
                                    </div>
                                    <div>
                                      <label className="text-sm">Título</label>
                                      <Input value={it?.title || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "title", e.target.value)} placeholder="Ej: Paga mucho menos cada vez que vas al médico" />
                                    </div>
                                    <div>
                                      <label className="text-sm">Descripción</label>
                                      <Textarea value={it?.description || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "description", e.target.value)} rows={3} placeholder="Descripción de la propuesta de valor..." />
                                    </div>
                                  </div>

                                  <div className="flex justify-end">
                                    <Button type="button" variant="destructive" onClick={() => removeSectionItemByIndex(section.id, itemIndex)}>
                                      Eliminar
                                    </Button>
                                  </div>
                                </>
                              ) : element === "info-cards" ? (
                                <>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="text-sm">ID</label>
                                      <Input value={it?.id || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "id", e.target.value)} />
                                    </div>
                                    <div>
                                      <label className="text-sm">Nombre del Plan</label>
                                      <Input value={it?.planName || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "planName", e.target.value)} />
                                    </div>
                                    <div className="md:col-span-2">
                                      <label className="text-sm">Imagen URL</label>
                                      <Input value={it?.imageurl || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "imageurl", e.target.value)} placeholder="https://.../imagen.jpg" />
                                    </div>
                                    {it?.imageurl && (
                                      <div className="md:col-span-2">
                                        <label className="text-sm">Posición de Imagen</label>
                                        <Select value={it?.imagePosition || "center"} onValueChange={(value) => updateSectionItemByIndex(section.id, itemIndex, "imagePosition", value)}>
                                          <SelectTrigger>
                                            <SelectValue placeholder="Selecciona posición" />
                                          </SelectTrigger>
                                          <SelectContent className="max-h-[220px] overflow-y-auto">
                                            {IMAGE_POSITION_OPTIONS.map((opt) => (
                                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    )}
                                  </div>

                                  <div className="space-y-2">
                                    <div className="text-sm font-medium">Requisitos</div>
                                    <div className="space-y-2">
                                      {(it?.requirements || []).map((req, reqIndex) => (
                                        <div key={`${it?.id || itemIndex}-req-${reqIndex}`} className="flex gap-2">
                                          <Input value={req || ""} onChange={(e) => updateSectionItemRequirementAt(section.id, itemIndex, reqIndex, e.target.value)} />
                                          <Button type="button" variant="destructive" size="sm" onClick={() => removeSectionItemRequirementAt(section.id, itemIndex, reqIndex)}>
                                            ✕
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                    <Button type="button" variant="outline" size="sm" onClick={() => addSectionItemRequirement(section.id, itemIndex)}>
                                      Agregar requisito
                                    </Button>
                                  </div>
                                </>
                              ) : (
                                /* Campos estándar para otros tipos */
                                <>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="text-sm">ID</label>
                                      <Input value={it?.id || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "id", e.target.value)} />
                                    </div>
                                    <div>
                                      <label className="text-sm">Icono</label>
                                      <Input value={it?.icon || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "icon", e.target.value)} />
                                    </div>
                                    <div className="md:col-span-2">
                                      <label className="text-sm">Título</label>
                                      <Input value={it?.title || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "title", e.target.value)} />
                                    </div>
                                    <div className="md:col-span-2">
                                      <label className="text-sm">Imagen URL</label>
                                      <Input value={it?.imageurl || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "imageurl", e.target.value)} placeholder="https://.../imagen.jpg" />
                                    </div>
                                    {it?.imageurl && (
                                      <div className="md:col-span-2">
                                        <label className="text-sm">Posición de Imagen</label>
                                        <Select value={it?.imagePosition || "center"} onValueChange={(value) => updateSectionItemByIndex(section.id, itemIndex, "imagePosition", value)}>
                                          <SelectTrigger>
                                            <SelectValue placeholder="Selecciona posición" />
                                          </SelectTrigger>
                                          <SelectContent className="max-h-[220px] overflow-y-auto">
                                            {IMAGE_POSITION_OPTIONS.map((opt) => (
                                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    )}
                                  </div>

                                  <div>
                                    <label className="text-sm">Descripción</label>
                                    <Textarea rows={4} value={it?.description || ""} onChange={(e) => updateSectionItemByIndex(section.id, itemIndex, "description", e.target.value)} />
                                  </div>

                                  <div className="space-y-2">
                                    <div className="text-sm">Highlights</div>
                                    <div className="space-y-2">
                                      {(it?.highlights || []).map((h, highlightIndex) => (
                                        <div key={`${it?.id || itemIndex}-h-${highlightIndex}`} className="flex gap-2">
                                          <Input value={h || ""} onChange={(e) => updateSectionItemHighlightAt(section.id, itemIndex, highlightIndex, e.target.value)} />
                                          <Button type="button" variant="destructive" size="sm" onClick={() => removeSectionItemHighlightAt(section.id, itemIndex, highlightIndex)}>
                                            ✕
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                    <Button type="button" variant="outline" size="sm" onClick={() => addSectionItemHighlight(section.id, itemIndex)}>
                                      Agregar highlight
                                    </Button>
                                  </div>
                                </>
                              )}

                              <div className="flex justify-end">
                                <Button type="button" variant="destructive" onClick={() => removeSectionItemByIndex(section.id, itemIndex)}>
                                  Eliminar ítem
                                </Button>
                              </div>
                            </div>

                            <div>
                              <div className="text-sm mb-2">Previsualización</div>
                              {element === "info-cards" ? (
                                <Card>
                                  <CardHeader>
                                    <CardTitle className={highlightClass || ""}>{it?.planName}</CardTitle>
                                  </CardHeader>
                                  <CardContent className="space-y-3">
                                    {it?.imageurl && (
                                      <img
                                        src={it.imageurl}
                                        alt={it?.planName || "imagen"}
                                        className="w-full h-32 object-cover rounded"
                                        style={{ objectPosition: getObjectPositionValue(it?.imagePosition || "center") }}
                                      />
                                    )}
                                    <ul className="text-sm list-disc pl-5 space-y-1">
                                      {(it?.requirements || []).map((r, idx) => (
                                        <li key={idx}>{r}</li>
                                      ))}
                                    </ul>
                                  </CardContent>
                                </Card>
                              ) : (
                                <ItemCard item={it} highlightClass={highlightClass} />
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}

                      <div className="pt-1">
                        <Button type="button" variant="outline" onClick={() => addSectionItem(section.id)}>
                          Agregar ítem
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
